import { useEffect } from "react";
import Button from "components/common/controls/Button";
import PlanCard from "./PlanCard";
import { useState } from "react";
import { getProductDetailWithLicenseAndFeatures } from "store/slices/ProductSlice";
import { connect } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import ProductLicense from "interfaces/response/ProductLicense";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  cancelScheduledSubscriptionChangeAsync,
  changeExistingSubscription,
  getSubscription,
  previewChangeSubscription,
  registerSubscriptionWithSelectedProduct,
  updateCurrentPeriodEndTimeWhenBillingIntervalIsChanging,
} from "store/slices/SubscriptionSlice";
import Subscription from "interfaces/response/Subscription";
import {
  getCurrentProductSubscriptionDetail,
  getCurrentProductLicenses,
  getSelectedProductId,
  getCurrentSubscriptionPlanLicense,
  getDefaultFreeLiceneOfSelectedProduct,
} from "shared/utility/ProductSubscriptionHelper";
import ChangeSubscriptionRequest from "interfaces/request/ChangeSubscriptionRequest";
import SubscriptionResponse from "interfaces/response/SubscriptionResponse";
import { toastError, toastSuccess } from "components/common/ToastNotification";
import Loader from "components/common/controls/Loader";
import { useIsSuccessQueryParam } from "shared/hooks/IsSuccessQueryParamGetter";
import ContactSalesPopup from "fetaures/contact-sales/ContactSalesPopup";
import PromineoSwitch from "components/common/controls/PromineoSwitch";
import { BillingInterval } from "shared/enums/BillingIntervalEnum";
import { checkIfIsEnterpriseLicense } from "shared/utility/ProductLicenseHelper";
import WidgetPanel from "components/common/WidgetPanel";
import { getFormattedUtcToLocal } from "shared/utility/DateTimeUtility";
import Link from "components/common/LinkButton";
import { addYearsWithGivenDate } from "shared/utility/DateTimeUtility";
import NextPaymentDetails from "interfaces/response/NextPaymentDetails";
import { NextPaymentDetailsType } from "shared/enums/NextPaymentDetailsTypeEnum";
import SubscriptionConfirmationPopup from "../subscription-confirmation/SunscriptionConfirmationPopup";
import SubscriptionChangePreviewResponse from "interfaces/response/SubscriptionChangePreviewResponse";
import SubscriptionScheduledOperations from "interfaces/response/SubscriptionScheduledOperations";
import StripeCheckoutRedirection from "./StripeCheckoutRedirection";
import CancelScheduledChangeConfirmation from "../subscription-confirmation/CancelScheduledChangeConfirmation";

interface Props {
  availablePlans?: ProductLicense[];
  productSubscriptionDetail?: Subscription | null;
  selectedProductId?: number;
  isContactToSalesPending?: boolean;
  dispatch?: AppDispatch;
  currentProductLicense?: ProductLicense;
  defaultFreeLicense?: ProductLicense;
  nextPaymentDetails: NextPaymentDetails;
  subscriptionChangePreview?: SubscriptionChangePreviewResponse | null;
  subscriptionScheduledOperations?: SubscriptionScheduledOperations | null;
  scheduledSubscriptionLicense?: ProductLicense;
}

export function SubscriptionPlanChooser(props: Props) {
  const {
    availablePlans,
    productSubscriptionDetail,
    selectedProductId,
    isContactToSalesPending,
    dispatch,
    currentProductLicense,
    defaultFreeLicense,
    nextPaymentDetails,
    subscriptionChangePreview,
    subscriptionScheduledOperations,
    scheduledSubscriptionLicense,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProductLicense | null>(null);
  const [
    isSubscriptionChangeConfirmationPopupVisible,
    setIsSubscriptionChangeConfirmationPopupVisible,
  ] = useState(false);
  const [
    isSubscriptionCancelConfirmationPopupVisible,
    setIsSubscriptionCancelConfirmationPopupVisible,
  ] = useState(false);
  const [isBillingPeriodFilterEnabled, setIsBillingPeriodFilterEnabled] =
    useState(false);
  const [plansToDisplay, setPlansToDisplay] = useState<ProductLicense[]>([]);
  const [isContactSalesPopupVisible, setIsContactSalesPopupVisible] =
    useState(false);
  const [
    isCancelScheduledChangeConfirmationPopupVisible,
    setIsCancelScheduledChangeConfirmationPopupVisible,
  ] = useState(false);

  const [
    isYearlyBillingPeriodInSelection,
    setIsYearlyBillingPeriodInSelection,
  ] = useState(false);

  const [
    subscriptionChangeResponseForCheckout,
    setSubscriptionChangeResponseForCheckout,
  ] = useState<SubscriptionResponse | null>(null);

  const [isSuccessQueryInUrl, isSuccess] = useIsSuccessQueryParam();
  useEffect(() => {
    if (isSuccessQueryInUrl) {
      isSuccess
        ? toastSuccess("Payment successful.")
        : toastError("Payment failed.");
    }
  }, [isSuccessQueryInUrl, isSuccess]);

  useEffect(() => {
    if (selectedProductId) {
      dispatch?.(getProductDetailWithLicenseAndFeatures(selectedProductId));
    }
  }, [dispatch, selectedProductId]);

  useEffect(() => {
    if (scheduledSubscriptionLicense) {
      if (
        scheduledSubscriptionLicense.billingInterval === BillingInterval.Yearly
      ) {
        setIsYearlyBillingPeriodInSelection(true);
      }
    } else if (
      currentProductLicense &&
      currentProductLicense.billingInterval === BillingInterval.Yearly
    ) {
      setIsYearlyBillingPeriodInSelection(true);
    }
  }, [currentProductLicense, scheduledSubscriptionLicense]);

  useEffect(() => {
    let freeLicense;
    let enterpriseLicense;
    let paidOnlineLicenses: ProductLicense[] = [];
    let isMonthlyPlanAvailable = false;
    let isYearlyPlanAvailable = false;

    // The free license & enterprise license will be always displayed irrespective of the filter (if not already selected)
    // and we want to display free license first and then online paid licenses and enterprise licenses at the end.
    (availablePlans || []).forEach((license) => {
      if (license.id !== productSubscriptionDetail?.productLicenseId) {
        if (license.isDefaultFreeLocalLicense) {
          freeLicense = license;
        } else if (checkIfIsEnterpriseLicense(license)) {
          enterpriseLicense = license;
        } else if (license.billingInterval === BillingInterval.Monthly) {
          if (!isYearlyBillingPeriodInSelection) {
            paidOnlineLicenses.push(license);
          }
          isMonthlyPlanAvailable = true;
        } else if (license.billingInterval === BillingInterval.Yearly) {
          if (isYearlyBillingPeriodInSelection) {
            paidOnlineLicenses.push(license);
          }
          isYearlyPlanAvailable = true;
        }
      }
    });

    if (freeLicense) {
      paidOnlineLicenses.unshift(freeLicense);
    }

    if (enterpriseLicense) {
      paidOnlineLicenses.push(enterpriseLicense);
    }

    if (isYearlyPlanAvailable && isMonthlyPlanAvailable) {
      setIsBillingPeriodFilterEnabled(true);
    } else if (isYearlyPlanAvailable) {
      // In billing period filter switch, the default selection is Monthly. If monthly licenses
      // are not available and yearly licenses are available, we need to set default selection Yearly.
      setIsYearlyBillingPeriodInSelection(true);
    }

    setPlansToDisplay(paidOnlineLicenses);
  }, [
    availablePlans,
    productSubscriptionDetail?.productLicenseId,
    isYearlyBillingPeriodInSelection,
  ]);

  const checkIfSelectedPlanIsEnterprise = () => {
    return selectedItem && checkIfIsEnterpriseLicense(selectedItem);
  };

  const handleSubscriptionUpdateResponse = (subscriptionResponse: any) => {
    try {
      const updatedSubscriptionResponse: SubscriptionResponse =
        unwrapResult(subscriptionResponse);

      if (updatedSubscriptionResponse.scheduledOn) {
        const scheduledOn = getFormattedUtcToLocal(
          updatedSubscriptionResponse.scheduledOn
        );
        // We need to get the subscription in order to show Next Payment details
        dispatch?.(getSubscription(updatedSubscriptionResponse.id)).finally(
          () => {
            toastSuccess(`Downgrade scheduled on ${scheduledOn}`);
          }
        );
      } else {
        if (updatedSubscriptionResponse.redirectToCheckoutSession) {
          setSubscriptionChangeResponseForCheckout(updatedSubscriptionResponse);
          // Page will be reloaded after checkout failiure or success. So, no need to reload subscription detail manually.
        } else {
          let isCurrentPeriodFinishDateChanging = false;
          const currentSubscriptionEndPeriod =
            productSubscriptionDetail?.currentPeriodFinish;

          // We need to change CurrentPeriodFinish date manually only for the first time if
          // we are changing from Monthly subscription to Yearly subscription
          if (
            currentProductLicense?.billingInterval ===
              BillingInterval.Monthly &&
            selectedItem?.billingInterval === BillingInterval.Yearly
          ) {
            isCurrentPeriodFinishDateChanging = true;
          }

          // Customizable(not enterprise) subscription's number of actions are in subscription level(not in license).
          // For displaying number of allowed action in current plan card, we need to reload updated subscription.
          dispatch?.(getSubscription(updatedSubscriptionResponse.id))
            .then(() => {
              if (isCurrentPeriodFinishDateChanging) {
                const updatedSubscriptionChangeDate = addYearsWithGivenDate(
                  currentSubscriptionEndPeriod!,
                  1
                );
                dispatch?.(
                  updateCurrentPeriodEndTimeWhenBillingIntervalIsChanging(
                    updatedSubscriptionChangeDate
                  )
                );
              }
            })
            .finally(() => {
              if (updatedSubscriptionResponse.hasNewPendingInvoice) {
                // The subscription is already updated successfully but the payment has failed
                // Subscription is already in grace period that's why API does not return error
                // We would like to inform user about failed payment
                toastError(
                  "Subscription plan is changed but is in the grace period due to payment failure."
                );
              } else {
                toastSuccess("Subscription plan is changed.");
              }
            });
        }
      }
    } finally {
      setIsLoading(false);
      setSelectedItem(null);
    }
  };

  const getSubscriptionCreateOrUpdateRequest = (licenseId: number) => {
    const requestModel: ChangeSubscriptionRequest = {
      licenseId,
      paymentSuccessCallbackUrl: window.location.href,
      paymentCancelCallbackUrl: window.location.href,
    };
    return requestModel;
  };

  const handleChangeSubscriptionConfirmation = (
    previewData: SubscriptionChangePreviewResponse | null
  ) => {
    setIsSubscriptionChangeConfirmationPopupVisible(false);
    if (selectedItem && productSubscriptionDetail && previewData) {
      setIsLoading(true);
      const subscriptionRequest = getSubscriptionCreateOrUpdateRequest(
        selectedItem.id
      );

      if (previewData.prorationAnchorTime) {
        subscriptionRequest.prorationAnchorTime =
          previewData.prorationAnchorTime;
      }

      dispatch?.(
        changeExistingSubscription({
          subscriptionId: productSubscriptionDetail.id,
          subscriptionChangeRequest: subscriptionRequest,
        })
      ).then(handleSubscriptionUpdateResponse);
    }
  };

  const changeSubscription = () => {
    if (productSubscriptionDetail && selectedItem) {
      if (checkIfSelectedPlanIsEnterprise()) {
        setIsContactSalesPopupVisible(true);
      } else {
        setIsLoading(true);
        const subscriptionRequest = getSubscriptionCreateOrUpdateRequest(
          selectedItem.id
        );

        dispatch?.(
          previewChangeSubscription({
            subscriptionId: productSubscriptionDetail.id,
            subscriptionChangeRequest: subscriptionRequest,
          })
        )
          .then(() => {
            setIsSubscriptionChangeConfirmationPopupVisible(true);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  };

  const createNewSubscription = () => {
    if (selectedItem) {
      const subscriptionRequest = getSubscriptionCreateOrUpdateRequest(
        selectedItem.id
      );
      dispatch?.(
        registerSubscriptionWithSelectedProduct(subscriptionRequest)
      ).then(handleSubscriptionUpdateResponse);
    }
  };

  const handleChangePlanClick = () => {
    if (selectedItem) {
      if (productSubscriptionDetail) {
        changeSubscription();
      } else {
        createNewSubscription();
      }
    }
  };

  const planSelectionChanged = (productLicense: ProductLicense) => {
    setSelectedItem(productLicense);
  };

  const handleContactSalesPopupClose = () => {
    setIsContactSalesPopupVisible(false);
    setSelectedItem(null);
  };

  const handleLicenseTypeChange = (value: boolean) => {
    setIsYearlyBillingPeriodInSelection(value);
  };

  const handleCancelSubscriptionClick = () => {
    if (defaultFreeLicense && productSubscriptionDetail) {
      setIsSubscriptionCancelConfirmationPopupVisible(true);
    }
  };

  const handleCancelSubscriptionChangeClick = () => {
    if (productSubscriptionDetail?.id) {
      setIsCancelScheduledChangeConfirmationPopupVisible(true);
    }
  };

  const handleCancelSubscriptionConfirm = () => {
    setIsSubscriptionCancelConfirmationPopupVisible(false);
    if (defaultFreeLicense && productSubscriptionDetail) {
      setIsLoading(true);
      const subscriptionRequest = getSubscriptionCreateOrUpdateRequest(
        defaultFreeLicense.id
      );
      dispatch?.(
        changeExistingSubscription({
          subscriptionId: productSubscriptionDetail.id,
          subscriptionChangeRequest: subscriptionRequest,
        })
      ).then(handleSubscriptionUpdateResponse);
    }
  };

  const PlanChooserFooter = () => {
    const getSubscriptionChooserButtonText = () => {
      if (checkIfSelectedPlanIsEnterprise()) {
        return "CONTACT SALES";
      } else {
        if (productSubscriptionDetail) {
          return "CHANGE PLAN";
        }
        return "CREATE SUBSCRIPTION";
      }
    };

    const CheckIfCurrentSubscriptionCanBeCanceled = () => {
      if (nextPaymentDetails.type === NextPaymentDetailsType.Active) {
        return (
          <div>
            <Link onClick={handleCancelSubscriptionClick}>
              Cancel subscription
            </Link>{" "}
            - Revert to a free account any time.
          </div>
        );
      } else {
        return <></>;
      }
    };

    const DisplayCurrentSubscriptionPlanStatus = () => {
      if (nextPaymentDetails.type === NextPaymentDetailsType.Downgrade) {
        return (
          <div className="block md:flex items-center justify-center">
            <div className="text-center">
              You have{" "}
              <span className="text-promBlue1 font-bold"> DOWNGRADED </span>{" "}
              your plan
              <div className="hidden md:inline">&nbsp;|&nbsp;</div>
            </div>
            <div className="text-center">
              takes effect: {getFormattedUtcToLocal(nextPaymentDetails.date)}
            </div>
          </div>
        );
      } else if (
        nextPaymentDetails.type === NextPaymentDetailsType.PaymentDue
      ) {
        return (
          <>
            <div className="block md:flex items-center justify-center">
              <div className="text-center">
                Your subscription has been{" "}
                <span className="text-promRed1 font-bold"> PAUSED </span>
                <div className="hidden md:inline">&nbsp;|&nbsp;</div>
              </div>
              <div className="text-center">cause: payment failure</div>
            </div>
            <div className="text-promGray2 mt-4 md:mt-0 text-center">
              Please resolve payment failure in order to change subscription
              plan
            </div>
          </>
        );
      }
      return <></>;
    };

    return (
      <div className="flex justify-center md:justify-between items-center flex-col md:flex-row">
        <div className="order-2 md:order-1 my-2 sm:my-0 text-sm text-promGray2">
          <CheckIfCurrentSubscriptionCanBeCanceled />
        </div>

        <div className="flex items-center order-1 md:order-2 flex-col md:flex-row">
          <div className="my-2 sm:my-0 text-sm px-6 order-2 md:order-1">
            <DisplayCurrentSubscriptionPlanStatus />
          </div>

          <div className="order-1 md:order-2 mb-4 md:mb-0">
            {subscriptionScheduledOperations && (
              <Button
                text="CANCEL CHANGE"
                onClick={handleCancelSubscriptionChangeClick}
              />
            )}
            {!subscriptionScheduledOperations && (
              <div className="block md:flex items-center">
                {!checkIfPendingPaymentExist() ? (
                  <div className="text-sm justify-center mr-0 sm:mr-6 hidden md:inline">
                    Select your desired plan
                  </div>
                ) : (
                  ""
                )}

                <Button
                  text={getSubscriptionChooserButtonText()}
                  onClick={handleChangePlanClick}
                  disabled={!selectedItem}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const checkIfPendingPaymentExist = () => {
    return (
      nextPaymentDetails &&
      (nextPaymentDetails.type === NextPaymentDetailsType.GracePeriod ||
        nextPaymentDetails.type === NextPaymentDetailsType.PaymentDue)
    );
  };

  const PlanChooserCards = () => {
    const checkIfPlanCardShouldDisabled = () => {
      return !!subscriptionScheduledOperations || checkIfPendingPaymentExist();
    };
    return (
      <>
        {plansToDisplay.map((productLicense: ProductLicense) => {
          return (
            <div key={productLicense.id}>
              <PlanCard
                key={productLicense.id}
                isAutomaticPurchaseAllowed={
                  productLicense.isAutomaticPurchaseAllowed
                }
                actions={productLicense.numberOfAllowedActions || 0}
                price={productLicense.price || 0}
                tag={productLicense.tag}
                onClick={() => planSelectionChanged(productLicense)}
                isActive={
                  productLicense.id === selectedItem?.id ||
                  (!!subscriptionScheduledOperations &&
                    subscriptionScheduledOperations.requestedProductLicenseId ===
                      productLicense.id)
                }
                billingInterval={productLicense.billingInterval}
                isDisabled={checkIfPlanCardShouldDisabled()}
              />
              {subscriptionScheduledOperations?.requestedProductLicenseId ===
                productLicense.id && (
                <div className="leading-3 text-primaryRed text-xs text-center w-full font-semibold">
                  new plan
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const BillingPeriodFilterSwitch = () => {
    return (
      <>
        <span className="mr-2 text-sm text-promGray2 font-medium">
          Billing period:
        </span>
        {isBillingPeriodFilterEnabled ? (
          <PromineoSwitch
            defaultValue={isYearlyBillingPeriodInSelection}
            onCaption={BillingInterval[BillingInterval.Yearly]}
            offCaption={BillingInterval[BillingInterval.Monthly]}
            onValueChange={handleLicenseTypeChange}
          />
        ) : (
          <span className="text-sm">
            {isYearlyBillingPeriodInSelection
              ? BillingInterval[BillingInterval.Yearly]
              : BillingInterval[BillingInterval.Monthly]}
          </span>
        )}
      </>
    );
  };

  const handleCancelScheduleSubscriptionChangeConfirm = () => {
    if (productSubscriptionDetail?.id) {
      dispatch?.(
        cancelScheduledSubscriptionChangeAsync(productSubscriptionDetail.id)
      ).finally(() => {
        setIsCancelScheduledChangeConfirmationPopupVisible(false);
      });
    }
  };

  return (
    <WidgetPanel
      title={
        productSubscriptionDetail
          ? "Change Subscription"
          : "Create Subscription"
      }
      headerWidget={<BillingPeriodFilterSwitch />}
      displaySecondaryHeaderOnSmallScreen
      titleForSmallScreen={"How many daily actions do you need?"}
      widgetInNewLineForSmallScreen
    >
      <div>
        <div className="grid grid-flow-row grid-cols-1 xsm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5px lg:gap-4">
          <PlanChooserCards />
        </div>
        <div className="mt-6 lg:mt-30px text-right">
          <PlanChooserFooter />
        </div>
      </div>
      <Loader isVisible={isLoading || isContactToSalesPending} />
      {isContactSalesPopupVisible && (
        <ContactSalesPopup
          productId={selectedProductId!}
          onClose={handleContactSalesPopupClose}
        />
      )}
      {isSubscriptionChangeConfirmationPopupVisible &&
        subscriptionChangePreview && (
          <SubscriptionConfirmationPopup
            onSubscriptionChangeConfirm={handleChangeSubscriptionConfirmation}
            onClose={() =>
              setIsSubscriptionChangeConfirmationPopupVisible(false)
            }
          />
        )}
      {isSubscriptionCancelConfirmationPopupVisible && defaultFreeLicense && (
        <SubscriptionConfirmationPopup
          isCancel
          defaultFreeLicense={defaultFreeLicense}
          onSubscriptionCancelConfirm={handleCancelSubscriptionConfirm}
          onClose={() => setIsSubscriptionCancelConfirmationPopupVisible(false)}
        />
      )}
      {isCancelScheduledChangeConfirmationPopupVisible && (
        <CancelScheduledChangeConfirmation
          onConfirm={handleCancelScheduleSubscriptionChangeConfirm}
          onCancel={() =>
            setIsCancelScheduledChangeConfirmationPopupVisible(false)
          }
        />
      )}
      {subscriptionChangeResponseForCheckout && (
        <StripeCheckoutRedirection
          sessionId={subscriptionChangeResponseForCheckout.checkoutSessionId}
        />
      )}
    </WidgetPanel>
  );
}

const mapStateToProps = (state: RootState) => {
  const availablePlans = getCurrentProductLicenses(state);
  const productSubscriptionDetail = getCurrentProductSubscriptionDetail(state);
  const currentProductLicense = getCurrentSubscriptionPlanLicense(state);
  const defaultFreeLicense = getDefaultFreeLiceneOfSelectedProduct(state);
  const isContactToSalesPending = state.contactSalesData.loading;
  const subscriptionScheduledOperations = state.subscriptionData
    .selectedProductSubscription?.subscriptionScheduledOperations
    ? state.subscriptionData.selectedProductSubscription
        .subscriptionScheduledOperations[0]
    : null;
  const subscriptionChangePreview =
    state.subscriptionData.subscriptionChangePreview;

  let scheduledSubscriptionLicense: null | ProductLicense = null;
  if (
    subscriptionScheduledOperations?.requestedProductLicenseId &&
    availablePlans.length
  ) {
    scheduledSubscriptionLicense =
      availablePlans.find(
        (ap) =>
          ap.id === subscriptionScheduledOperations?.requestedProductLicenseId
      ) || null;
  }

  return {
    availablePlans,
    productSubscriptionDetail,
    selectedProductId: getSelectedProductId(state),
    isContactToSalesPending,
    currentProductLicense,
    defaultFreeLicense,
    subscriptionChangePreview,
    subscriptionScheduledOperations,
    scheduledSubscriptionLicense,
  };
};

export default connect(mapStateToProps, null)(SubscriptionPlanChooser);
